import { db } from "../config";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
  addDoc,
  deleteDoc,
  increment,
  orderBy,
} from "firebase/firestore";
import { getBookById, getOrCreateBook } from "./bookServices";
import { getUserById } from "./userServices";
import { updateGoalProgress } from "./readingGoalsServices";
import { createActivity, getBookActivities } from "./activityServices";
import { ActivityType } from "../constants/activityTypes";
import { ReadingFormat } from "../constants/readingFormats";

const USER_BOOKS_COLLECTION = "UserBooks";
const READING_LISTS_COLLECTION = "ReadingLists";

// Add or update a book's reading status for a user
export const updateReadingStatus = async (
  userId,
  bookId,
  status,
  remove = false,
  format = ReadingFormat.PAPERBACK
) => {
  try {
    const userBookRef = doc(db, USER_BOOKS_COLLECTION, `${userId}_${bookId}`);
    const userBookDoc = await getDoc(userBookRef);
    const bookDoc = await getDoc(doc(db, "Books", bookId));
    const userRef = doc(db, "Users", userId);
    const bookPages = bookDoc.exists() ? bookDoc.data().pages : 0;

    if (remove) {
      if (userBookDoc.exists()) {
        const oldStatus = userBookDoc.data().status;
        if (oldStatus === "Read") {
          await updateDoc(userRef, {
            pagesRead: increment(-bookPages),
          });
        }
        await deleteDoc(userBookRef);

        // Create REMOVED activity
        try {
          await createActivity({
            userId,
            bookId,
            activityType: ActivityType.REMOVED,
            date: new Date().toISOString(),
            metadata: {
              fromStatusUpdate: true,
              oldStatus: oldStatus || null,
              newStatus: null,
            },
            visibility: "public",
          });
        } catch (activityError) {
          console.error("Error creating removal activity:", activityError);
        }
      }
      return null;
    }

    const existingData = userBookDoc.exists() ? userBookDoc.data() : {};
    const oldStatus = existingData.status;
    const isNewBook = !userBookDoc.exists();
    const statusChanged = oldStatus !== status;

    // Only update pages read if:
    // 1. Moving to "Read" status from another status
    // 2. Moving from "Read" status to another status
    // 3. Book wasn't previously in user's library
    if (!userBookDoc.exists() && status === "Read") {
      await updateDoc(userRef, {
        pagesRead: increment(bookPages),
      });
    } else if (oldStatus !== status) {
      if (status === "Read") {
        await updateDoc(userRef, {
          pagesRead: increment(bookPages),
        });
        // Add goal updates when marking as read
        await updateGoalProgress(userId, {
          type: "books",
          amount: 1,
        });
        await updateGoalProgress(userId, {
          type: "pages",
          amount: bookPages,
        });
      } else if (oldStatus === "Read") {
        await updateDoc(userRef, {
          pagesRead: increment(-bookPages),
        });
        // Subtract from goals when unmarking as read
        await updateGoalProgress(userId, {
          type: "books",
          amount: -1,
        });
        await updateGoalProgress(userId, {
          type: "pages",
          amount: -bookPages,
        });
      }
    }

    const bookData = {
      ...existingData,
      userId,
      bookId,
      status,
      updatedAt: new Date().toISOString(),
    };

    if (!userBookDoc.exists()) {
      bookData.createdAt = new Date().toISOString();
      bookData.readThroughCount = 1;
    }

    await setDoc(userBookRef, bookData, { merge: true });

    // Dual-write: Create activity in ReadingActivities collection
    try {
      let activityType = null;
      const now = new Date().toISOString();

      // Determine which activity to create based on status
      if (isNewBook || statusChanged) {
        // Fetch existing activities to check for rereads
        const existingActivities = await getBookActivities(userId, bookId);
        const finishedActivities = existingActivities.filter(
          (a) => a.activityType === ActivityType.FINISHED
        );
        const isReread = finishedActivities.length > 0;
        const readThroughNumber = finishedActivities.length + 1;

        if (status === "Want to Read") {
          activityType = ActivityType.WANT_TO_READ;
        } else if (status === "Currently Reading") {
          // Use RESUMED if coming from "Read" or "Paused" status, otherwise STARTED
          if (oldStatus === "Read" || oldStatus === "Paused") {
            activityType = ActivityType.RESUMED;
          } else {
            activityType = ActivityType.STARTED;
          }
          // Track start date for Currently Reading books
          if (!existingData.startDate) {
            await updateDoc(userBookRef, { startDate: now });
            bookData.startDate = now;
          }
        } else if (status === "Read") {
          activityType = ActivityType.FINISHED;
        } else if (status === "Did Not Finish") {
          activityType = ActivityType.DNF;
        } else if (status === "Paused") {
          activityType = ActivityType.PAUSED;
        }

        if (activityType) {
          const metadata = {
            fromStatusUpdate: true,
            oldStatus: oldStatus || null,
            newStatus: status,
            format: format, // Use provided format or default
          };

          // Add reread metadata for FINISHED and STARTED activities
          if (activityType === ActivityType.FINISHED && isReread) {
            metadata.isReread = true;
            metadata.readThroughNumber = readThroughNumber;
          } else if (activityType === ActivityType.STARTED && isReread) {
            metadata.isReread = true;
            metadata.readThroughNumber = readThroughNumber;
          }

          await createActivity({
            userId,
            bookId,
            activityType,
            date: now,
            metadata,
            visibility: "public",
          });
        }
      }
    } catch (activityError) {
      // Log but don't fail the whole operation if activity creation fails
      console.error("Error creating activity:", activityError);
    }

    return bookData;
  } catch (error) {
    console.error("Error updating reading status:", error);
    throw error;
  }
};

// Get a user's reading status for a specific book
export const getReadingStatus = async (userId, bookId) => {
  try {
    const userBookRef = doc(db, USER_BOOKS_COLLECTION, `${userId}_${bookId}`);
    const userBookDoc = await getDoc(userBookRef);

    if (userBookDoc.exists()) {
      return userBookDoc.data();
    }
    return null;
  } catch (error) {
    console.error("Error getting reading status:", error);
    throw error;
  }
};

// Get all books with a specific status for a user
export const getBooksByStatus = async (userId, status) => {
  try {
    const q = query(
      collection(db, USER_BOOKS_COLLECTION),
      where("userId", "==", userId),
      where("status", "==", status)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => doc.data());
  } catch (error) {
    console.error("Error getting books by status:", error);
    throw error;
  }
};

// Create a new reading list
export const createReadingList = async (userId, name) => {
  try {
    // Get user data first
    const creator = await getUserById(userId);

    const listData = {
      userId,
      name,
      nameLower: name.toLowerCase(),
      books: [],
      creator: {
        uid: creator.uid,
        username: creator.username,
        displayName: creator.displayName,
        profileImage: creator.profileImage || null,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const docRef = await addDoc(
      collection(db, READING_LISTS_COLLECTION),
      listData
    );
    return { id: docRef.id, ...listData };
  } catch (error) {
    console.error("Error creating reading list:", error);
    throw error;
  }
};

// Add a book to a reading list
export const addBookToList = async (listId, bookId) => {
  try {
    const existingBook = await getBookById(bookId);
    if (!existingBook) {
      const response = await fetch(
        `https://www.googleapis.com/books/v1/volumes/${bookId}`
      );
      const googleBook = await response.json();
      await getOrCreateBook(googleBook);
    }

    // Get list details for activity creation
    const listRef = doc(db, READING_LISTS_COLLECTION, listId);
    const listDoc = await getDoc(listRef);

    if (!listDoc.exists()) {
      throw new Error("List not found");
    }

    const listData = listDoc.data();

    await updateDoc(listRef, {
      books: arrayUnion(bookId),
      updatedAt: new Date().toISOString(),
    });

    // Create SHELVED activity
    try {
      await createActivity({
        userId: listData.userId,
        bookId: bookId,
        activityType: ActivityType.SHELVED,
        date: new Date().toISOString(),
        metadata: {
          listId: listId,
          listName: listData.name,
        },
        visibility: "public",
      });
    } catch (activityError) {
      // Log but don't fail the operation - activity can be recreated if needed
      console.error("Error creating shelved activity:", activityError);
    }
  } catch (error) {
    console.error("Error adding book to list:", error);
    throw error;
  }
};

// Get all reading lists for a user
export const getUserLists = async (userId) => {
  try {
    const q = query(
      collection(db, READING_LISTS_COLLECTION),
      where("userId", "==", userId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting user lists:", error);
    throw error;
  }
};

// Get all books for a user with their reading status
export const getUserBooks = async (userId) => {
  try {
    const q = query(
      collection(db, USER_BOOKS_COLLECTION),
      where("userId", "==", userId)
    );
    const querySnapshot = await getDocs(q);
    const userBooks = querySnapshot.docs.map((doc) => doc.data());

    // Fetch full book details for each book
    const booksWithDetails = await Promise.all(
      userBooks.map(async (userBook) => {
        const bookDetails = await getBookById(userBook.bookId);
        return {
          ...bookDetails,
          status: userBook.status,
          pagesRead: userBook.pagesRead,
          updatedAt: userBook.updatedAt,
        };
      })
    );

    return booksWithDetails;
  } catch (error) {
    console.error("Error getting user books:", error);
    throw error;
  }
};

// Get books for a specific list
export const getListBooks = async (listId) => {
  try {
    const listRef = doc(db, READING_LISTS_COLLECTION, listId);
    const listDoc = await getDoc(listRef);

    if (!listDoc.exists()) {
      throw new Error("List not found");
    }

    const list = listDoc.data();
    const bookIds = list.books;

    // Fetch full book details for each book in the list
    const books = await Promise.all(
      bookIds.map(async (bookId) => {
        let book = await getBookById(bookId);

        // If book doesn't exist in our cache, try to fetch and store it
        if (!book) {
          try {
            const response = await fetch(
              `https://www.googleapis.com/books/v1/volumes/${bookId}`
            );
            const googleBook = await response.json();

            if (response.ok) {
              book = await getOrCreateBook(googleBook);
            } else {
              console.error(
                `Failed to fetch book ${bookId}:`,
                googleBook.error
              );
              return null;
            }
          } catch (error) {
            console.error(`Error fetching book ${bookId}:`, error);
            return null;
          }
        }

        return book;
      })
    );

    return books.filter(Boolean);
  } catch (error) {
    console.error("Error getting list books:", error);
    throw error;
  }
};

// Get reading statistics for a user
export const getUserReadingStats = async (userId) => {
  try {
    const userBooks = await getUserBooks(userId);
    const readingProgress = [];

    // Get all reading progress entries for the user
    const q = query(
      collection(db, "ReadingProgress"),
      where("userId", "==", userId),
      orderBy("readDate", "desc")
    );

    const snapshot = await getDocs(q);
    snapshot.forEach((doc) => {
      const data = doc.data();
      readingProgress.push({
        date: data.readDate,
        pages: data.currentPage,
        timeSpent: data.timeSpent,
      });
    });

    return {
      totalBooks: userBooks.length,
      booksRead: userBooks.filter((book) => book.status === "Read").length,
      currentlyReading: userBooks.filter(
        (book) => book.status === "Currently Reading"
      ).length,
      wantToRead: userBooks.filter((book) => book.status === "Want to Read")
        .length,
      paused: userBooks.filter((book) => book.status === "Paused").length,
      dnf: userBooks.filter((book) => book.status === "Did Not Finish").length,
      totalPagesRead: userBooks.reduce(
        (total, book) => total + (book.pagesRead || 0),
        0
      ),
      readingProgress, // Add reading progress data for heatmap
    };
  } catch (error) {
    console.error("Error getting user reading stats:", error);
    throw error;
  }
};

// Follow a list
export const followList = async (userId, listId) => {
  try {
    const userListRef = doc(db, "UserListFollows", `${userId}_${listId}`);
    const listRef = doc(db, READING_LISTS_COLLECTION, listId);

    await setDoc(userListRef, {
      userId,
      listId,
      createdAt: new Date().toISOString(),
    });

    // Increment followers count
    await updateDoc(listRef, {
      followers: increment(1),
    });
  } catch (error) {
    console.error("Error following list:", error);
    throw error;
  }
};

// Unfollow a list
export const unfollowList = async (userId, listId) => {
  try {
    const userListRef = doc(db, "UserListFollows", `${userId}_${listId}`);
    const listRef = doc(db, READING_LISTS_COLLECTION, listId);

    await deleteDoc(userListRef);

    // Decrement followers count
    await updateDoc(listRef, {
      followers: increment(-1),
    });
  } catch (error) {
    console.error("Error unfollowing list:", error);
    throw error;
  }
};

// Check if user follows a list
export const checkListFollow = async (userId, listId) => {
  try {
    const userListRef = doc(db, "UserListFollows", `${userId}_${listId}`);
    const docSnapshot = await getDoc(userListRef);
    return docSnapshot.exists();
  } catch (error) {
    console.error("Error checking list follow:", error);
    throw error;
  }
};

// Get a list with all its details
export const getListDetails = async (listId) => {
  try {
    const listRef = doc(db, READING_LISTS_COLLECTION, listId);
    const listDoc = await getDoc(listRef);

    if (!listDoc.exists()) {
      throw new Error("List not found");
    }

    const listData = listDoc.data();
    const creator = await getUserById(listData.userId);
    const books = await getListBooks(listId);

    // Add metadata for sorting
    const booksWithMeta = books.map((book) => ({
      ...book,
      dateAdded: book.createdAt || new Date().toISOString(),
      pageCount: book.pageCount || 0,
      publishedDate: book.publishedDate || "",
      averageRating: book.rating || 0,
    }));

    return {
      id: listDoc.id,
      ...listData,
      creator,
      books: booksWithMeta,
    };
  } catch (error) {
    console.error("Error getting list details:", error);
    throw error;
  }
};

// Get all lists containing a specific book
export const getListsContainingBook = async (userId, bookId) => {
  try {
    const q = query(
      collection(db, READING_LISTS_COLLECTION),
      where("userId", "==", userId),
      where("books", "array-contains", bookId)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting lists containing book:", error);
    throw error;
  }
};

// Update list order
export const updateListOrder = async (listId, orderedBookIds) => {
  try {
    const listRef = doc(db, READING_LISTS_COLLECTION, listId);
    await updateDoc(listRef, {
      books: orderedBookIds,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error updating list order:", error);
    throw error;
  }
};

// Get lists that a user follows
export const getFollowedLists = async (userId) => {
  try {
    const q = query(
      collection(db, "UserListFollows"),
      where("userId", "==", userId)
    );
    const querySnapshot = await getDocs(q);

    // Get full list details for each followed list
    const lists = await Promise.all(
      querySnapshot.docs.map(async (docSnapshot) => {
        const listId = docSnapshot.data().listId;
        const listDoc = await getDoc(doc(db, READING_LISTS_COLLECTION, listId));
        if (!listDoc.exists()) return null;

        const listData = listDoc.data();
        const creator = await getUserById(listData.userId);
        const books = await getListBooks(listId);

        return {
          id: listDoc.id,
          ...listData,
          creator,
          books,
          followedAt: docSnapshot.data().createdAt,
        };
      })
    );

    return lists.filter(Boolean); // Remove any null values
  } catch (error) {
    console.error("Error getting followed lists:", error);
    throw error;
  }
};

// Add a function to update list name
export const updateListName = async (listId, newName) => {
  try {
    const listRef = doc(db, READING_LISTS_COLLECTION, listId);
    await updateDoc(listRef, {
      name: newName,
      nameLower: newName.toLowerCase(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error updating list name:", error);
    throw error;
  }
};

// Add a function to update existing lists with lowercase names
export const updateExistingListsWithLowercase = async () => {
  try {
    const listsRef = collection(db, READING_LISTS_COLLECTION);
    const querySnapshot = await getDocs(listsRef);

    const updatePromises = querySnapshot.docs.map(async (doc) => {
      const listData = doc.data();
      if (listData.name && !listData.nameLower) {
        const listRef = doc(db, READING_LISTS_COLLECTION, doc.id);
        await updateDoc(listRef, {
          nameLower: listData.name.toLowerCase(),
        });
      }
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error("Error updating existing lists:", error);
    throw error;
  }
};

// Add a function to update creator info in lists
export const updateListCreatorInfo = async (userId, userData) => {
  try {
    // Get all lists by this user
    const q = query(
      collection(db, READING_LISTS_COLLECTION),
      where("userId", "==", userId)
    );
    const querySnapshot = await getDocs(q);

    const updatePromises = querySnapshot.docs.map(async (doc) => {
      const listRef = doc.ref;
      await updateDoc(listRef, {
        creator: {
          uid: userId,
          username: userData.username,
          displayName: userData.displayName,
          profileImage: userData.profileImage || null,
        },
        updatedAt: new Date().toISOString(),
      });
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error("Error updating list creator info:", error);
    throw error;
  }
};

// Add a function to update existing lists with creator info
export const updateExistingListsWithCreatorInfo = async () => {
  try {
    const listsRef = collection(db, READING_LISTS_COLLECTION);
    const querySnapshot = await getDocs(listsRef);

    const updatePromises = querySnapshot.docs.map(async (doc) => {
      const listData = doc.data();
      if (!listData.creator && listData.userId) {
        try {
          const creator = await getUserById(listData.userId);
          const listRef = doc.ref;
          await updateDoc(listRef, {
            creator: {
              uid: creator.uid,
              username: creator.username,
              displayName: creator.displayName,
              profileImage: creator.profileImage || null,
            },
            updatedAt: new Date().toISOString(),
          });
        } catch (error) {
          console.error(`Error updating list ${doc.id}:`, error);
        }
      }
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error("Error updating existing lists:", error);
    throw error;
  }
};

export const deleteList = async (listId) => {
  try {
    const listRef = doc(db, READING_LISTS_COLLECTION, listId);
    await deleteDoc(listRef);
  } catch (error) {
    console.error("Error deleting list:", error);
    throw error;
  }
};
